import axios, { type AxiosError, type AxiosRequestConfig } from 'axios';
import { toast } from 'sonner';

import { trackEvent } from '~/lib/analytics';
import { fireQuotaExceeded } from '~/lib/quota-events';

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5392/api';

/**
 * @deprecated Cookie is now set by the backend via Set-Cookie header.
 * Kept as no-op so existing call sites compile without changes.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function syncAuthCookie(_token: string | null) {
  // no-op — backend sets httpOnly auth-token cookie on login/register
}

export const api = axios.create({
  baseURL: BASE_URL,
  timeout: 120000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config) => {
    if (typeof window !== 'undefined') {
      const token = localStorage.getItem('auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      config.headers['x-request-id'] = crypto.randomUUID();
    }
    return config;
  },
  (error) => Promise.reject(error)
);

let onUnauthorized: (() => void) | undefined;
let has401Fired = false;

/** Register a callback to invoke on 401 responses (called by store init) */
export function setOnUnauthorized(cb: () => void) {
  onUnauthorized = cb;
}

/** Reset the 401 dedup flag (call on login/navigation to login page) */
export function reset401Flag() {
  has401Fired = false;
}

api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const status = error.response?.status;
    if (status === 401 && typeof window !== 'undefined') {
      localStorage.removeItem('auth_token');
      if (!has401Fired) {
        has401Fired = true;
        // Clear httpOnly cookie via backend logout (fire-and-forget, once)
        api.post('/auth/logout').catch(() => {});
        onUnauthorized?.();
        trackEvent('auth_expired');
        toast.error('Session expired — please sign in again');
      }
    } else if (status === 403 && typeof window !== 'undefined') {
      const data = error.response?.data as Record<string, unknown> | undefined;
      if (data?.errorCode === 'BIZ_4003') {
        // Quota exceeded — fire upsell modal instead of generic error
        const details = data.details as { resource?: string; current?: number; limit?: number } | undefined;
        fireQuotaExceeded({
          resource: (details?.resource as string) ?? 'items',
          current: (details?.current as number) ?? 0,
          limit: (details?.limit as number) ?? 0,
          message: (data.message as string) ?? 'You\'ve reached your plan limit.',
        });
        trackEvent('quota_exceeded', {
          resource: details?.resource ?? 'unknown',
          endpoint: error.config?.url ?? 'unknown',
        });
      } else {
        trackEvent('api_error', {
          endpoint: error.config?.url ?? 'unknown',
          status_code: status,
        });
      }
    } else if (status && status !== 401) {
      trackEvent('api_error', {
        endpoint: error.config?.url ?? 'unknown',
        status_code: status,
      });
    }
    return Promise.reject(error);
  }
);

export interface ApiResponse<T> {
  data: T;
  meta?: {
    timestamp: string;
    path: string;
  };
}

export async function get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const response = await api.get<ApiResponse<T>>(url, config);
  return response.data.data;
}

export async function post<T, D = unknown>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await api.post<ApiResponse<T>>(url, data, config);
  return response.data.data;
}

export async function put<T, D = unknown>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await api.put<ApiResponse<T>>(url, data, config);
  return response.data.data;
}

export async function patch<T, D = unknown>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await api.patch<ApiResponse<T>>(url, data, config);
  return response.data.data;
}

export async function del<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const response = await api.delete<ApiResponse<T>>(url, config);
  return response.data.data;
}

/** Build asset preview URL (used by img src, no auth needed currently) */
export function assetPreviewUrl(assetId: string): string {
  return `${BASE_URL}/assets/${assetId}/preview`;
}

/** Build project logo URL (streams from GCS or redirects to external URL) */
export function logoPreviewUrl(projectId: string): string {
  return `${BASE_URL}/projects/${projectId}/logo`;
}

/** Resolve the best logo URL for a project from its settings */
export function getProjectLogoUrl(project: { id: string; settings?: Record<string, unknown> }): string | undefined {
  const s = project.settings;
  if (!s) return undefined;
  if (s.logoObjectKey) return logoPreviewUrl(project.id);
  if (s.logoUrl && typeof s.logoUrl === 'string') return s.logoUrl;
  return undefined;
}

export default api;
